// Cell-fracture chunk pool. Polygon rubble that spawns when a brick breaks,
// tumbles with gravity, bounces, settles on the paddle and ground, then fades.
//
// SoA layout, swap-remove on death, fused step+render. Decoupled from the
// pixel-particle system: chunks need rotation + polygon fill, which is wholly
// incompatible with the 1px ImageData fast path.

import {
  type Pattern,
  type Cell,
  getPattern,
  impactZoneOf,
  INTENSITY_LOW, INTENSITY_HIGH,
  VARIANTS_PER_BUCKET,
  patternIndex,
  getAtlas,
} from './cell-atlas.js';

export interface ChunkPoolEnv {
  paddleCx: number;
  paddleHalfW: number;
  paddleTopY: number;
  paddleH: number;
  groundY: number;
  worldH: number;
  /** World-to-canvas scale (i.e. what `ctx.scale(s, s)` was set to). */
  canvasScale: number;
  /** World-to-canvas translation X (post-shake). */
  canvasOffsetX: number;
  canvasOffsetY: number;
}

export interface FractureRequest {
  brickX: number;
  brickY: number;
  brickW: number;
  brickH: number;
  brickColorABGR: number;       // packed ABGR (alpha byte ignored)
  impactX: number;              // world coords
  impactY: number;
  impactVx: number;             // typically ball velocity pre-reflection
  impactVy: number;
  /** ~1..3 chaos multiplier — scales speed and life. */
  chaos: number;
  /** 0..2 (low/mid/high). High used for chaos peak / fireball / overdrive. */
  intensity: number;
  /** 0..3 — variant rotation, caller picks (e.g. bricksBroken & 3). */
  variant: number;
}

// ---- Tuning -----------------------------------------------------------------

export const CHUNK_TUNING = {
  baseSpeed: 90,
  speedFromImpactKey: 220,
  speedFromChaos: 60,
  ballMomentumTransfer: 0.18,
  angVelMax: 12,
  baseLife: 1.2,
  lifeJitter: 0.9,
  lifeFromImpactKey: 0.4,
  airDrag: 0.992,
  gravity: 720,
  bounceChance: 0.4,
  bounceRestitution: 0.35,
  restingDrag: 0.78,
  restingSpinDrag: 0.86,
  /** Extra per-second life decay when over visible-resting budget. */
  overflowFade: 3.0,
  lightAmplitude: 0.22,
  /** Fade-out window as fraction of total life. */
  fadeFraction: 0.45,
  /** Brightness boost on close-to-impact chunks (multiplier on RGB at spawn). */
  brightnessBoost: 0.28,
} as const;

const REST_AIR = 0;
const REST_GROUND = 1;
const REST_PADDLE = 2;

const SHADE_BUCKETS = 12;
const ALPHA_BUCKETS = 16;
const BOOST_BUCKETS = 6;        // brightness boost quantization
const TWO_PI = Math.PI * 2;

// Pre-computed shade factor per angle bucket. lightAngle is fixed at -π/2 so
// cos(angle - lightAngle) reduces to sin(angle), then we modulate by amplitude
// around 1.0 and clamp.
const SHADE_FACTORS = new Float32Array(SHADE_BUCKETS);
for (let i = 0; i < SHADE_BUCKETS; i++) {
  const a = (i + 0.5) * (TWO_PI / SHADE_BUCKETS);
  const s = Math.sin(a) * CHUNK_TUNING.lightAmplitude + 1;
  SHADE_FACTORS[i] = s < 0.5 ? 0.5 : s > 1.2 ? 1.2 : s;
}

function angleToShadeBucket(angle: number): number {
  let a = angle % TWO_PI;
  if (a < 0) a += TWO_PI;
  const b = (a * (SHADE_BUCKETS / TWO_PI)) | 0;
  return b >= SHADE_BUCKETS ? 0 : b;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

// ---- Color string cache -----------------------------------------------------
//
// `ctx.fillStyle = '#rrggbb'` accepts a string. Building "rgba(r,g,b,a)" per
// draw allocates GC churn. We cache by quantized (rgb, alphaBucket, shadeBucket,
// boostBucket). Realistic distinct entries: a few thousand — fits in a Map
// without bloating, and the hit-rate is ~100% after one wave.

const _colorCache = new Map<number, string>();
const ALPHA_DIM = ALPHA_BUCKETS;
const SHADE_DIM = SHADE_BUCKETS;
const BOOST_DIM = BOOST_BUCKETS;

function colorString(
  rgb: number,           // baseColorABGR & 0x00ffffff
  alphaBucket: number,
  shadeBucket: number,
  boostBucket: number,
): string {
  // Composite key via multiplication (safe-integer range, no 32-bit shift overflow).
  const key = rgb * (ALPHA_DIM * SHADE_DIM * BOOST_DIM)
    + alphaBucket * (SHADE_DIM * BOOST_DIM)
    + shadeBucket * BOOST_DIM
    + boostBucket;
  const cached = _colorCache.get(key);
  if (cached !== undefined) return cached;
  const r0 = rgb & 0xff;
  const g0 = (rgb >>> 8) & 0xff;
  const b0 = (rgb >>> 16) & 0xff;
  const shade = SHADE_FACTORS[shadeBucket] ?? 1;
  const boost = 1 + (boostBucket / (BOOST_DIM - 1)) * CHUNK_TUNING.brightnessBoost;
  const m = shade * boost;
  const r = clamp8(r0 * m);
  const g = clamp8(g0 * m);
  const b = clamp8(b0 * m);
  const alpha = (alphaBucket + 0.5) / ALPHA_DIM;
  const s = `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
  _colorCache.set(key, s);
  return s;
}

// ---- Pool -------------------------------------------------------------------

export class ChunkPool {
  readonly cap: number;
  count = 0;

  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly angle: Float32Array;
  readonly angVel: Float32Array;
  readonly life: Float32Array;
  readonly invMaxLife: Float32Array;
  readonly patternIdx: Uint16Array;
  readonly cellIdx: Uint16Array;
  readonly scaleW: Float32Array;
  readonly scaleH: Float32Array;
  readonly color: Uint32Array;
  readonly brightness: Uint8Array;
  readonly restingOn: Uint8Array;
  readonly paddleOffsetX: Float32Array;
  readonly paddleOffsetY: Float32Array;

  constructor(cap = 2048) {
    this.cap = cap;
    this.x = new Float32Array(cap);
    this.y = new Float32Array(cap);
    this.vx = new Float32Array(cap);
    this.vy = new Float32Array(cap);
    this.angle = new Float32Array(cap);
    this.angVel = new Float32Array(cap);
    this.life = new Float32Array(cap);
    this.invMaxLife = new Float32Array(cap);
    this.patternIdx = new Uint16Array(cap);
    this.cellIdx = new Uint16Array(cap);
    this.scaleW = new Float32Array(cap);
    this.scaleH = new Float32Array(cap);
    this.color = new Uint32Array(cap);
    this.brightness = new Uint8Array(cap);
    this.restingOn = new Uint8Array(cap);
    this.paddleOffsetX = new Float32Array(cap);
    this.paddleOffsetY = new Float32Array(cap);
    // Eagerly build the atlas so the first break never jank-spikes.
    getAtlas();
  }

  clear(): void {
    this.count = 0;
  }

  spawnFracture(req: FractureRequest): void {
    if (this.count >= this.cap) return;

    // Auto-degrade intensity at high pool occupancy. Pool stays useful under
    // load instead of dropping spawns entirely.
    let intensity = req.intensity | 0;
    if (intensity > INTENSITY_LOW && this.count > this.cap * 0.5) intensity -= 1;
    if (intensity > INTENSITY_LOW && this.count > this.cap * 0.75) intensity = INTENSITY_LOW;
    if (intensity < INTENSITY_LOW) intensity = INTENSITY_LOW;
    if (intensity > INTENSITY_HIGH) intensity = INTENSITY_HIGH;

    const localX = (req.impactX - req.brickX) / req.brickW;
    const localY = (req.impactY - req.brickY) / req.brickH;
    const zone = impactZoneOf(
      localX < 0 ? 0 : localX > 1 ? 1 : localX,
      localY < 0 ? 0 : localY > 1 ? 1 : localY,
    );
    const variant = (req.variant | 0) & (VARIANTS_PER_BUCKET - 1);
    const pIdx = patternIndex(zone, intensity, variant);
    const pattern: Pattern = getPattern(zone, intensity, variant);
    const order = pattern.indexByBrightness;

    const tuning = CHUNK_TUNING;
    const chaos = req.chaos;
    const wx0 = req.brickX;
    const wy0 = req.brickY;
    const bw = req.brickW;
    const bh = req.brickH;
    const color = req.brickColorABGR;
    const room = this.cap - this.count;
    const limit = order.length < room ? order.length : room;

    for (let i = 0; i < limit; i++) {
      const ci = order[i] as number;
      const cell = pattern.cells[ci] as Cell;
      const wcx = wx0 + cell.cx * bw;
      const wcy = wy0 + cell.cy * bh;
      let ox = wcx - req.impactX;
      let oy = wcy - req.impactY;
      const len = Math.sqrt(ox * ox + oy * oy);
      if (len > 1e-4) {
        ox /= len; oy /= len;
      } else {
        const a = Math.random() * TWO_PI;
        ox = Math.cos(a); oy = Math.sin(a);
      }
      const bk = cell.brightnessKey;
      const speed = tuning.baseSpeed
        + tuning.speedFromImpactKey * bk
        + tuning.speedFromChaos * (chaos - 1);
      const jitterA = (Math.random() - 0.5) * 0.6;
      const cs = Math.cos(jitterA);
      const sn = Math.sin(jitterA);
      const dx = ox * cs - oy * sn;
      const dy = ox * sn + oy * cs;
      const vx = dx * speed + req.impactVx * tuning.ballMomentumTransfer;
      // Slight upward bias on every chunk — sells the "blown apart" feel
      // before gravity takes over.
      const vy = dy * speed + req.impactVy * tuning.ballMomentumTransfer - 40;
      const angVel = (Math.random() - 0.5) * tuning.angVelMax * (0.5 + bk);
      const lifeBase = tuning.baseLife + tuning.lifeFromImpactKey * bk;
      const life = lifeBase + Math.random() * tuning.lifeJitter;

      const j = this.count++;
      this.x[j] = wcx;
      this.y[j] = wcy;
      this.vx[j] = vx;
      this.vy[j] = vy;
      this.angle[j] = (Math.random() - 0.5) * 0.3;
      this.angVel[j] = angVel;
      this.life[j] = life;
      this.invMaxLife[j] = 1 / life;
      this.patternIdx[j] = pIdx;
      this.cellIdx[j] = ci;
      this.scaleW[j] = bw;
      this.scaleH[j] = bh;
      this.color[j] = color;
      this.brightness[j] = (bk * 255) | 0;
      this.restingOn[j] = REST_AIR;
      this.paddleOffsetX[j] = 0;
      this.paddleOffsetY[j] = 0;
      if (this.count >= this.cap) break;
    }
  }

  /** Step physics + render in a single fused pass. dt=0 freezes (paused). */
  stepAndRender(
    dt: number,
    ctx: CanvasRenderingContext2D,
    env: ChunkPoolEnv,
  ): void {
    const tuning = CHUNK_TUNING;
    const atlas = getAtlas();
    const fade = tuning.fadeFraction;
    const fadeInv = 1 / (1 - fade);

    // Count resting chunks to decide whether to accelerate fade-out for the pile.
    let restingCount = 0;
    if (dt > 0) {
      const rest = this.restingOn;
      const n0 = this.count;
      for (let k = 0; k < n0; k++) {
        if (rest[k] !== REST_AIR) restingCount++;
      }
    }
    const extraDecay = restingCount > 256 ? tuning.overflowFade : 0;

    let i = 0;
    let n = this.count;
    while (i < n) {
      let lifeI = this.life[i] as number;
      const invMax = this.invMaxLife[i] as number;
      let restI = this.restingOn[i] as number;
      let xI = this.x[i] as number;
      let yI = this.y[i] as number;
      let vxI = this.vx[i] as number;
      let vyI = this.vy[i] as number;
      let angI = this.angle[i] as number;
      let avI = this.angVel[i] as number;

      if (dt > 0) {
        const decay = restI !== REST_AIR ? dt * (1 + extraDecay) : dt;
        lifeI -= decay;
        if (lifeI <= 0 || yI > env.worldH + 64) {
          const last = --n;
          if (i !== last) {
            this.x[i] = this.x[last] as number; this.y[i] = this.y[last] as number;
            this.vx[i] = this.vx[last] as number; this.vy[i] = this.vy[last] as number;
            this.angle[i] = this.angle[last] as number; this.angVel[i] = this.angVel[last] as number;
            this.life[i] = this.life[last] as number; this.invMaxLife[i] = this.invMaxLife[last] as number;
            this.patternIdx[i] = this.patternIdx[last] as number;
            this.cellIdx[i] = this.cellIdx[last] as number;
            this.scaleW[i] = this.scaleW[last] as number;
            this.scaleH[i] = this.scaleH[last] as number;
            this.color[i] = this.color[last] as number;
            this.brightness[i] = this.brightness[last] as number;
            this.restingOn[i] = this.restingOn[last] as number;
            this.paddleOffsetX[i] = this.paddleOffsetX[last] as number;
            this.paddleOffsetY[i] = this.paddleOffsetY[last] as number;
          }
          continue;
        }

        if (restI === REST_AIR) {
          vyI += tuning.gravity * dt;
          vxI *= tuning.airDrag;
          vyI *= tuning.airDrag;
          xI += vxI * dt;
          yI += vyI * dt;
          angI += avI * dt;
          avI *= 0.995;

          if (yI >= env.groundY) {
            if (Math.abs(vyI) > 60 && Math.random() < tuning.bounceChance) {
              yI = env.groundY - 0.5;
              vyI = -Math.abs(vyI) * tuning.bounceRestitution;
              vxI *= 0.7;
              avI *= 0.5;
            } else {
              yI = env.groundY;
              vyI = 0;
              restI = REST_GROUND;
            }
          } else if (
            vyI > 0
            && yI >= env.paddleTopY - 1
            && yI <= env.paddleTopY + env.paddleH + 2
            && xI >= env.paddleCx - env.paddleHalfW
            && xI <= env.paddleCx + env.paddleHalfW
          ) {
            restI = REST_PADDLE;
            this.paddleOffsetX[i] = xI - env.paddleCx;
            this.paddleOffsetY[i] = env.paddleTopY - yI;
            vyI = 0;
            vxI *= 0.5;
            avI *= 0.4;
          }
        } else if (restI === REST_GROUND) {
          vxI *= tuning.restingDrag;
          avI *= tuning.restingSpinDrag;
          xI += vxI * dt;
          angI += avI * dt;
        } else {
          // REST_PADDLE — glue to paddle, gentle spin damp.
          xI = env.paddleCx + (this.paddleOffsetX[i] as number);
          yI = env.paddleTopY - (this.paddleOffsetY[i] as number);
          avI *= tuning.restingSpinDrag;
          angI += avI * dt;
        }

        this.life[i] = lifeI;
        this.x[i] = xI; this.y[i] = yI;
        this.vx[i] = vxI; this.vy[i] = vyI;
        this.angle[i] = angI; this.angVel[i] = avI;
        this.restingOn[i] = restI;
      }

      // Render
      const lt = lifeI * invMax;
      if (lt > 0) {
        let alphaF: number;
        if (lt >= 1 - fade) {
          alphaF = 1;
        } else {
          const t = lt * fadeInv;
          alphaF = t < 0 ? 0 : t > 1 ? 1 : t;
          alphaF = alphaF * alphaF * (3 - 2 * alphaF);
        }
        const alphaBucket = ((alphaF * (ALPHA_BUCKETS - 1)) | 0);
        if (alphaBucket > 0) {
          const shadeBucket = angleToShadeBucket(angI);
          // Boost fades with life — close-to-impact chunks glow hot at spawn.
          const boostF = ((this.brightness[i] as number) / 255) * lt;
          const boostBucket = (boostF * (BOOST_BUCKETS - 1)) | 0;
          const rgb = (this.color[i] as number) & 0x00ffffff;
          const str = colorString(rgb, alphaBucket, shadeBucket, boostBucket);

          const sw = this.scaleW[i] as number;
          const sh = this.scaleH[i] as number;
          const cs = Math.cos(angI);
          const sn = Math.sin(angI);
          const wS = env.canvasScale;
          const wOx = env.canvasOffsetX;
          const wOy = env.canvasOffsetY;
          ctx.setTransform(
            wS * sw * cs, wS * sw * sn,
            wS * (-sh) * sn, wS * sh * cs,
            wOx + wS * xI, wOy + wS * yI,
          );
          ctx.fillStyle = str;
          const pat = atlas[this.patternIdx[i] as number];
          if (pat !== undefined) {
            const cell = pat.cells[this.cellIdx[i] as number];
            if (cell !== undefined) ctx.fill(cell.path);
          }
        }
      }
      i++;
    }
    this.count = n;
    // Restore the world transform so callers can continue drawing in world space.
    const wS = env.canvasScale;
    ctx.setTransform(wS, 0, 0, wS, env.canvasOffsetX, env.canvasOffsetY);
  }
}
